import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import axios from 'axios';

import { REACT_APP_API_HOST as API_HOST } from 'envs';
import { loginState } from 'global-stores';
import { API_URL } from 'constant';
import { useCookie, useRefreshCookie } from 'hooks/cookie';
import { moveToLoginPage } from 'hooks/components';
import { useNotification } from 'hooks/notification';

export const useUpdateUserToken = () => {
	const { accessToken } = useRecoilValue(loginState);
	const { set: setCookie, deleteCookie } = useCookie();
	const {errorNotification} = useNotification()
	const { get: getRefreshCookie, deleteCookie: deleteRefreshCookie } =
		useRefreshCookie();
	const setLoginState = useSetRecoilState(loginState);

	const updateToken = useCallback(async () => {
		/* NOTHING TO REFRESH WITH IS NOT A REQUEST TO MAKE.
		 *
		 * This posted whatever the cookie held, including `null` when there is
		 * no refresh cookie at all -- and the server answers
		 * 400 `"refreshToken" must be a string`. It happens on every 401 for a
		 * session that has an access token but no refresh cookie, which is the
		 * normal state whenever the `user` cookie was set directly rather than
		 * earned through the login form. On dev2 that was 348 rejected requests
		 * in a day, each one an error line describing a request that could
		 * never have succeeded.
		 *
		 * Without a refresh token the only honest outcome is the one the catch
		 * below already implements, so go there directly instead of asking the
		 * server to tell us what we can see for ourselves.
		 */
		const refreshToken = getRefreshCookie('auth_refresh_token');
		if (!refreshToken) {
			/* CANNOT REFRESH IS NOT THE SAME AS MUST LOG OUT.
			 *
			 * An earlier version of this guard cleared the session and
			 * redirected here, which was wrong and broke every session whose
			 * cookie was set directly rather than earned through the login form:
			 * the panel logged ITSELF out on the first 401 from any endpoint,
			 * even when the access token was perfectly valid. The catch below
			 * deliberately does not log out when the server answers "Access
			 * token is not expired yet", and skipping straight to logout threw
			 * that distinction away.
			 *
			 * Returning false says exactly what is true -- there is nothing to
			 * refresh with -- and retryOperation, the only caller, simply does
			 * not retry. The request that triggered this still fails on its own
			 * merits, and a session that is genuinely dead still ends up at the
			 * login page through the normal path.
			 */
			return false;
		}
		try {
			const response = await axios.post(
				API_HOST + API_URL.VERIFY_REFRESH_TOKEN,
				{
					accessToken: accessToken,
					refreshToken,
				}
			);
			if (response?.data?.data?.accessToken) {
				setLoginState(prevState => ({
					...prevState,
					accessToken: response?.data?.data?.accessToken,
				}));
				const cookieData = {
					accessToken: response?.data?.data?.accessToken,
				};
				setCookie('user', { ...cookieData });
				return true;
			}
			return true;
		} catch (error:any) {
			if(error?.response?.data?.message !== 'Access token is not expired yet'){
				errorNotification('Logged in required !')
				await moveToLoginPage();
				deleteCookie('user');
				deleteCookie('userEmail');
				deleteRefreshCookie('auth_refresh_token');
				localStorage.clear();
				window.location.href = '/login';
			}
			
			return false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken]);

	return { updateToken };
};
